import React, { useState, useRef, useEffect, useCallback } from 'react';
import { opportunitiesApi } from '../api/client';
import { fs, makeFields, Getter, Setter } from './farFields';

/* Contract Administration & Closeout — post-award lifecycle stage.
 * Separate from FAR 12-15 (solicitation/award). Governed by FAR 42/43/46/32/49/4.804.
 * Lives inside the opportunity workspace; shown when the award is finalized.
 * Saved to opportunity.contract_admin (JSON). Reuses farFields primitives. */

// Configuration-controlled closeout time standards (FAR 4.804 / agency rules).
const CLOSEOUT_STANDARDS: Record<string, string> = {
  'Simplified acquisition': 'Close after evidence of receipt and final payment',
  'Firm-fixed-price (other than simplified)': 'Within 6 months after evidence of physical completion',
  'Requires settlement of indirect cost rates': 'Within 36 months after the month of physical completion',
  'Other contracts': 'Within 20 months after the month of physical completion',
};

const SUB_TABS = [
  { id: 'setup', label: '1. Contract Setup' },
  { id: 'performance', label: '2. Performance' },
  { id: 'funds', label: '3. Invoices & Funds' },
  { id: 'changes', label: '4. Changes & Options' },
  { id: 'issues', label: '5. Issues & Remedies' },
  { id: 'closeout', label: '6. Closeout' },
];

export default function ContractAdminTab({ opp, oppId, awardFinalized = false, onSaved }: { opp: any; oppId: string; awardFinalized?: boolean; onSaved?: (d: any) => void }) {
  const initial = (() => {
    try { const raw = opp?.contract_admin; return raw ? (typeof raw === 'string' ? JSON.parse(raw) : raw) : {}; } catch { return {}; }
  })();

  const [data, setData] = useState<Record<string, any>>(initial);
  const [tab, setTab] = useState('setup');
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [dirty, setDirty] = useState(false);
  const dirtyRef = useRef(false);
  const dataRef = useRef(data);
  useEffect(() => { dataRef.current = data; }, [data]);

  const markDirty = () => { if (!dirtyRef.current) { dirtyRef.current = true; setDirty(true); } };
  const set: Setter = (k, v) => { markDirty(); setData(prev => ({ ...prev, [k]: v })); };
  const get: Getter = (k, def = '') => (data[k] !== undefined ? data[k] : def);
  const F = makeFields(get, set);

  const save = useCallback(async () => {
    setSaving(true); setError(''); setMessage('');
    try {
      await opportunitiesApi.update(oppId, { contract_admin: JSON.stringify(data) });
      setMessage('Contract administration saved.');
      dirtyRef.current = false; setDirty(false);
      onSaved?.(data);
    } catch (e: any) { setError(e.message || 'Save failed.'); }
    setSaving(false);
  }, [data, oppId, onSaved]);

  useEffect(() => {
    const h = (e: BeforeUnloadEvent) => { if (dirtyRef.current) { e.preventDefault(); e.returnValue = ''; } };
    window.addEventListener('beforeunload', h);
    return () => window.removeEventListener('beforeunload', h);
  }, []);

  // ─── Closeout eligibility (system determination) ───
  const physicallyComplete = get('co_all_delivered') !== 'No' && get('co_all_services') !== 'No' && get('co_all_deliverables') === 'Yes';
  const blockers = get('co_litigation') === 'Yes' || get('co_termination_outstanding') === 'Yes';
  const eligibleForCloseout = physicallyComplete && !blockers;

  const CLOSEOUT_CHECKLIST = [
    'Final deliverables received and accepted.',
    'Final inspection and acceptance completed.',
    'Final invoice received.',
    'Final payment completed.',
    'Classified material disposition completed.',
    'Final patent report cleared, if applicable.',
    'Final royalty report cleared, if applicable.',
    'Government property clearance received.',
    'Plant clearance completed, if applicable.',
    'Value engineering proposals resolved.',
    'Subcontracts settled.',
    'Interim or disallowed costs settled.',
    'Indirect cost rates settled, if applicable.',
    'Price revisions completed.',
    'Contract audit completed, if required.',
    'Termination docket completed, if applicable.',
    'Contractor closing statement received.',
    'Funds review completed.',
    'Excess funds deobligated.',
    'Final performance evaluation completed.',
    'Claims, disputes, litigation, and appeals resolved.',
    'Security access and system accounts closed.',
    'Records retention classification assigned.',
    'Contract completion statement prepared.',
  ];

  return (
    <div style={{ maxWidth: 1000 }}>
      {/* Header + save bar */}
      <div style={{ position: 'sticky', top: 0, zIndex: 5, background: '#f4f6fa', padding: '8px 0', display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <div>
          <h2 style={{ fontSize: 18, margin: 0 }}>Contract Administration &amp; Closeout</h2>
          <p style={{ fontSize: 11, color: '#6b7280', margin: '2px 0 0' }}>Post-award lifecycle stage — FAR Parts 42, 43, 46, 32, 49 and FAR 4.804. Begins when the award is finalized.</p>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          {dirty && <span style={{ fontSize: 12, color: '#b45309', fontWeight: 600 }}>● Unsaved</span>}
          <button onClick={save} disabled={saving} style={{ padding: '10px 20px', background: saving ? '#86efac' : dirty ? '#7c3aed' : '#10b981', color: 'white', border: 'none', borderRadius: 6, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>{saving ? 'Saving...' : 'Save'}</button>
        </div>
      </div>
      {error && <div role="alert" style={{ padding: '10px 14px', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 6, color: '#991b1b', fontSize: 13, marginBottom: 12 }}>{error}</div>}
      {message && <div role="status" style={{ padding: '10px 14px', background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 6, color: '#166534', fontSize: 13, marginBottom: 12 }}>{message}</div>}
      {!awardFinalized && (
        <div style={{ padding: '10px 14px', background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 6, color: '#1e40af', fontSize: 12, marginBottom: 12 }}>
          This is the post-award stage. It formally begins once the award is finalized (opportunity status set to <strong>awarded</strong>). You can prepare the contract record here now; it becomes the active administration workspace after award.
        </div>
      )}

      {/* Sub-tab bar */}
      <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', borderBottom: '1px solid #e5e7eb', marginBottom: 16 }}>
        {SUB_TABS.map(t => (
          <button key={t.id} onClick={() => setTab(t.id)} style={{
            padding: '8px 14px', border: 'none', borderRadius: '6px 6px 0 0', cursor: 'pointer',
            fontSize: 12, fontWeight: tab === t.id ? 700 : 500,
            background: tab === t.id ? '#ede9fe' : 'transparent', color: tab === t.id ? '#5b21b6' : '#6b7280',
            borderBottom: tab === t.id ? '2px solid #7c3aed' : '2px solid transparent',
          }}>{t.label}</button>
        ))}
      </div>

      {/* ── Tab 1: Contract Setup ── */}
      {tab === 'setup' && (<>
        <div style={fs.section}>
          <h3 style={fs.title}>A. Contract Information</h3>
          <p style={fs.sub}>FAR Part 42.</p>
          {F.Text('contract_number', 'Contract Number', { req: true })}
          {F.Text('piid', 'Award ID / Procurement Instrument Identifier (PIID)', { req: true })}
          {F.Text('contract_title', 'Contract Title')}
          {F.Text('contractor', 'Contractor')}
          {F.Text('uei_cage', 'UEI and CAGE Code')}
          {F.Sel('contract_type', 'Contract Type', ['Firm-fixed-price', 'Fixed-price with economic price adjustment', 'Cost-reimbursement', 'Time-and-materials', 'Labor-hour', 'Indefinite-delivery', 'Other'])}
          <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
            {F.Money('award_amount', 'Award Amount')}
            {F.Money('funded_amount', 'Funded Amount')}
            {F.Money('ceiling_value', 'Ceiling Value')}
          </div>
          <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
            {F.Date_('award_date', 'Award Date')}
            {F.Date_('effective_date', 'Effective Date')}
          </div>
          <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
            {F.Date_('pop_start', 'Period of Performance — Start')}
            {F.Date_('pop_end', 'Period of Performance — End')}
          </div>
          {F.TableField('option_periods', 'Option Periods', [
            { key: 'option', label: 'Option', type: 'text' },
            { key: 'start', label: 'Start', type: 'text' },
            { key: 'end', label: 'End', type: 'text' },
            { key: 'value', label: 'Est. value', type: 'text' },
            { key: 'exercise_deadline', label: 'Exercise deadline', type: 'text' },
            { key: 'status', label: 'Status', type: 'select', options: ['Not exercised', 'Exercised', 'Expired', 'Declined'] },
          ])}
          {F.Text('award_document', 'Award Document (reference)')}
        </div>

        <div style={fs.section}>
          <h3 style={fs.title}>B. Administration Responsibilities</h3>
          {F.Text('co', 'Contracting Officer')}
          {F.Text('cs', 'Contract Specialist')}
          {F.Text('cor', "Contracting Officer's Representative (COR)")}
          {F.Text('pm', 'Program Manager')}
          {F.Text('invoice_approver', 'Invoice Approver')}
          {F.Text('property_admin', 'Property Administrator (when applicable)')}
          {F.Text('qar', 'Quality Assurance Representative (when applicable)')}
          {F.Text('cao', 'Contract Administration Office')}
          {F.Text('paying_office', 'Paying Office')}
          {F.ListField('delegated_functions', 'Delegated Administration Functions')}
          {F.Text('cor_letter', 'COR Designation Letter (reference)')}
          {F.YN('kickoff_required', 'Kickoff Meeting Required?')}
          {get('kickoff_required') === 'Yes' && F.Text('kickoff_record', 'Kickoff Date & Record')}
        </div>

        <div style={fs.section}>
          <h3 style={fs.title}>C. Administration Plan</h3>
          {F.YN('admin_plan_required', 'Contract Administration Plan Required?')}
          {F.Sel('reporting_frequency', 'Reporting Frequency', ['Weekly', 'Monthly', 'Quarterly', 'Milestone-based'])}
          {F.ListField('required_reports', 'Required Contractor Reports')}
          {F.MultiSelect('surveillance_method', 'Government Surveillance Method', ['Inspection', 'Testing', 'Periodic review', 'Deliverable review', 'Performance metrics', 'Quality Assurance Surveillance Plan', 'Other'])}
          {F.TableField('risk_register', 'Risk Register', [
            { key: 'risk', label: 'Risk', type: 'text' },
            { key: 'probability', label: 'Probability', type: 'select', options: ['Low', 'Medium', 'High'] },
            { key: 'impact', label: 'Impact', type: 'select', options: ['Low', 'Medium', 'High'] },
            { key: 'owner', label: 'Owner', type: 'text' },
            { key: 'mitigation', label: 'Mitigation', type: 'long' },
            { key: 'status', label: 'Status', type: 'select', options: ['Open', 'Monitoring', 'Closed'] },
          ])}
          {F.Sel('admin_plan_approval', 'Administration Plan Approval', ['Draft', 'In Review', 'Approved'])}
        </div>
      </>)}

      {/* ── Tab 2: Performance ── */}
      {tab === 'performance' && (<>
        <div style={fs.section}>
          <h3 style={fs.title}>A. Deliverables and Milestones</h3>
          <p style={fs.sub}>FAR Parts 42 & 46. The system flags upcoming, late, rejected, and unresolved deliverables.</p>
          {F.TableField('deliverables', 'Deliverables', [
            { key: 'title', label: 'Deliverable title', type: 'text' },
            { key: 'reference', label: 'Contract ref / CLIN', type: 'text' },
            { key: 'due', label: 'Due date', type: 'text' },
            { key: 'submission', label: 'Contractor submission', type: 'text' },
            { key: 'submitted', label: 'Submitted date', type: 'text' },
            { key: 'reviewer', label: 'Gov reviewer', type: 'text' },
            { key: 'review_deadline', label: 'Review deadline', type: 'text' },
            { key: 'status', label: 'Status', type: 'select', options: ['Pending', 'Submitted', 'In Review', 'Accepted', 'Rejected'] },
            { key: 'acceptance_date', label: 'Acceptance date', type: 'text' },
            { key: 'comments', label: 'Comments', type: 'long' },
          ])}
        </div>
        <div style={fs.section}>
          <h3 style={fs.title}>B. Inspection and Acceptance</h3>
          <div style={{ padding: '8px 12px', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 6, fontSize: 11, color: '#92400e', marginBottom: 12 }}>
            Only an authorized Government user should record final acceptance.
          </div>
          {F.YN('inspection_required', 'Inspection Required?')}
          {F.Text('inspection_location', 'Inspection Location')}
          {F.Sel('inspection_method', 'Inspection Method', ['Document review', 'Physical inspection', 'Functional test', 'Security test', 'Demonstration', 'Sampling', 'Other'])}
          {F.Long('acceptance_criteria', 'Acceptance Criteria')}
          {F.Sel('inspection_result', 'Inspection Result', ['Accepted', 'Conditionally Accepted', 'Rejected'])}
          {F.YN('nonconformance', 'Nonconformance Identified?')}
          {F.YN('corrective_action_req', 'Corrective Action Required?')}
          {F.Text('acceptance_official', 'Authorized Acceptance Official')}
          {F.Date_('acceptance_date', 'Acceptance Date')}
          {F.Text('acceptance_evidence', 'Acceptance Evidence (reference)')}
        </div>
        <div style={fs.section}>
          <h3 style={fs.title}>C. Performance Monitoring</h3>
          {F.TableField('performance_metrics', 'Performance Metrics', [
            { key: 'metric', label: 'Metric', type: 'text' },
            { key: 'target', label: 'Contract target', type: 'text' },
            { key: 'actual', label: 'Actual result', type: 'text' },
            { key: 'period', label: 'Period', type: 'text' },
            { key: 'status', label: 'Status', type: 'select', options: ['Met', 'At Risk', 'Not Met'] },
            { key: 'evidence', label: 'Evidence', type: 'text' },
          ])}
          {F.Sel('performance_status', 'Performance Status', ['On Track', 'At Risk', 'Unsatisfactory'])}
          {F.TableField('perf_meetings', 'Performance Review Meetings', [
            { key: 'date', label: 'Date', type: 'text' },
            { key: 'attendees', label: 'Attendees', type: 'text' },
            { key: 'summary', label: 'Summary', type: 'long' },
          ])}
          {F.TableField('perf_issues', 'Contractor Performance Issues', [
            { key: 'issue', label: 'Issue', type: 'long' },
            { key: 'severity', label: 'Severity', type: 'select', options: ['Low', 'Medium', 'High', 'Critical'] },
            { key: 'status', label: 'Status', type: 'select', options: ['Open', 'Monitoring', 'Resolved'] },
          ])}
          {F.YN('performance_assessment_req', 'Performance Assessment (CPARS) Required?')}
          {F.Sel('cpars_status', 'CPARS Evaluation Status', ['Not Due', 'Draft', 'Contractor Comments', 'Reviewed', 'Final'])}
          {F.TableField('performance_ratings', 'Performance Ratings', [
            { key: 'area', label: 'Rating area', type: 'text' },
            { key: 'rating', label: 'Rating', type: 'select', options: ['Exceptional', 'Very Good', 'Satisfactory', 'Marginal', 'Unsatisfactory'] },
            { key: 'narrative', label: 'Narrative', type: 'long' },
          ])}
        </div>
      </>)}

      {/* ── Tab 3: Invoices & Funds ── */}
      {tab === 'funds' && (<>
        <div style={fs.section}>
          <h3 style={fs.title}>A. Funding</h3>
          <p style={fs.sub}>FAR Part 32.</p>
          <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
            {F.Money('original_obligated', 'Original Obligated Amount')}
            {F.Money('contract_ceiling', 'Contract Ceiling')}
          </div>
          {F.TableField('funding_by_clin', 'Funding by CLIN', [
            { key: 'clin', label: 'CLIN', type: 'text' },
            { key: 'fund_citation', label: 'Fund citation', type: 'text' },
            { key: 'obligated', label: 'Obligated', type: 'text' },
            { key: 'invoiced', label: 'Invoiced', type: 'text' },
            { key: 'paid', label: 'Paid', type: 'text' },
            { key: 'remaining', label: 'Remaining', type: 'text' },
          ])}
          {F.YN('funding_risk', 'Funding Risk?', ['None', 'Low', 'Elevated'])}
        </div>
        <div style={fs.section}>
          <h3 style={fs.title}>B. Invoice Processing</h3>
          <div style={{ padding: '8px 12px', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 6, fontSize: 11, color: '#92400e', marginBottom: 12 }}>
            Do not approve an invoice when the associated supply/service has not been accepted, unless the contract permits another payment arrangement.
          </div>
          {F.TableField('invoices', 'Invoice Register', [
            { key: 'number', label: 'Invoice number', type: 'text' },
            { key: 'date', label: 'Invoice date', type: 'text' },
            { key: 'period', label: 'Billing period', type: 'text' },
            { key: 'amount', label: 'Amount', type: 'text' },
            { key: 'received', label: 'Received date', type: 'text' },
            { key: 'cor_validation', label: 'COR validation', type: 'select', options: ['Approved', 'Rejected', 'Pending'] },
            { key: 'acceptance_verified', label: 'Acceptance verified', type: 'select', options: ['Yes', 'No'] },
            { key: 'payment_status', label: 'Payment status', type: 'select', options: ['Pending', 'Paid', 'Rejected'] },
            { key: 'payment_date', label: 'Payment date', type: 'text' },
            { key: 'interest', label: 'Interest/discount', type: 'text' },
          ])}
          {F.YN('invoice_matches_perf', 'Invoice Matches Accepted Performance?')}
          {F.YN('invoice_correct', 'Invoice Mathematically Correct?')}
          {F.YN('funding_available', 'Funding Available?')}
          {F.Long('invoice_comments', 'Invoice Approval Comments')}
          {F.YN('final_invoice', 'Final Invoice?')}
        </div>
      </>)}

      {/* ── Tab 4: Changes & Options ── */}
      {tab === 'changes' && (<>
        <div style={{ padding: '8px 12px', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 6, fontSize: 12, color: '#991b1b', marginBottom: 12 }}>
          Only the Contracting Officer may issue or approve a contract modification. A COR or Program Manager cannot direct the contractor to perform changed work.
        </div>
        <div style={fs.section}>
          <h3 style={fs.title}>A. Change Request</h3>
          <p style={fs.sub}>FAR Parts 17 & 43.</p>
          {F.Text('cr_requested_by', 'Requested By')}
          {F.Long('cr_description', 'Change Description')}
          {F.Long('cr_business_need', 'Business Need')}
          {F.ListField('cr_affected', 'Affected Contract Sections or CLINs')}
          {F.Sel('cr_scope', 'Scope Determination', ['Within Scope', 'Potentially Out of Scope', 'Legal Review Required'])}
          {F.Money('cr_cost_impact', 'Estimated Cost Impact')}
          {F.Text('cr_schedule_impact', 'Schedule Impact')}
          {F.YN('cr_funding', 'Funding Available?', ['Yes', 'No', 'Pending'])}
          {F.YN('cr_proposal_req', 'Contractor Proposal Required?')}
          {F.Long('cr_analysis', 'Technical and Price Analysis')}
        </div>
        <div style={fs.section}>
          <h3 style={fs.title}>B. Contract Modification</h3>
          {F.Text('mod_number', 'Modification Number')}
          {F.Sel('mod_type', 'Modification Type', ['Administrative change', 'Bilateral modification', 'Unilateral modification', 'Change order', 'Funding modification', 'Option exercise', 'Termination modification', 'Other'])}
          {F.Text('mod_authority', 'Authority (clause/statutory reference)')}
          {F.Date_('mod_effective', 'Effective Date')}
          {F.Money('mod_amount', 'Amount Change')}
          {F.YN('mod_equitable_adj', 'Equitable Adjustment Required?')}
          {F.YN('mod_contractor_agreement', 'Contractor Agreement Required?')}
          {F.YN('mod_legal_review', 'Legal Review Required?')}
          {F.YN('mod_co_approval', 'Contracting Officer Approval Obtained?')}
          {F.Text('mod_signed', 'Signed Modification (reference)')}
        </div>
        <div style={fs.section}>
          <h3 style={fs.title}>C. Option Exercise</h3>
          {F.Text('opt_number', 'Option Number')}
          {F.Date_('opt_deadline', 'Option Exercise Deadline')}
          {F.YN('opt_continued_need', 'Continued Need Confirmed?')}
          {F.YN('opt_funds', 'Funds Available?')}
          {F.YN('opt_performance_ok', 'Contractor Performance Acceptable?')}
          {F.YN('opt_priced_at_award', 'Option Price Evaluated at Award?')}
          {F.YN('opt_most_advantageous', 'Option Remains Most Advantageous?')}
          {F.YN('opt_notice_provided', 'Required Notice Provided?')}
          {F.Long('opt_determination', 'Option Determination')}
          {F.YN('opt_mod_issued', 'Option Modification Issued?')}
        </div>
      </>)}

      {/* ── Tab 5: Issues & Remedies ── */}
      {tab === 'issues' && (<>
        <div style={fs.section}>
          <h3 style={fs.title}>A. Issue Register</h3>
          <p style={fs.sub}>FAR Parts 33, 46 & 49.</p>
          {F.TableField('issues', 'Issues', [
            { key: 'type', label: 'Issue type', type: 'select', options: ['Late delivery', 'Performance deficiency', 'Quality nonconformance', 'Security incident', 'Unauthorized commitment', 'Funding issue', 'Invoice dispute', 'Labor compliance', 'Property issue', 'Other'] },
            { key: 'description', label: 'Description', type: 'long' },
            { key: 'reference', label: 'Contract ref', type: 'text' },
            { key: 'severity', label: 'Severity', type: 'select', options: ['Low', 'Medium', 'High', 'Critical'] },
            { key: 'responsible', label: 'Responsible party', type: 'text' },
            { key: 'corrective', label: 'Corrective action', type: 'long' },
            { key: 'due', label: 'Due date', type: 'text' },
            { key: 'status', label: 'Status', type: 'select', options: ['Open', 'Monitoring', 'Resolved'] },
          ])}
        </div>
        <div style={fs.section}>
          <h3 style={fs.title}>B. Corrective Actions and Remedies</h3>
          {F.YN('ca_requested', 'Corrective Action Requested?')}
          {F.Long('ca_contractor_response', 'Contractor Response')}
          {F.YN('cure_notice', 'Cure Notice Required?')}
          {F.YN('show_cause', 'Show Cause Notice Required?')}
          {F.YN('withholding', 'Payment Withholding Considered?')}
          {F.YN('price_reduction', 'Price Reduction or Reperformance Required?')}
          {F.YN('liquidated_damages', 'Liquidated Damages Applicable?')}
          {F.Long('remedy_decision', 'Government Remedy Decision')}
          {F.Sel('remedy_legal_review', 'Legal Review', ['Not Required', 'Pending', 'Complete'])}
        </div>
        <div style={fs.section}>
          <h3 style={fs.title}>C. Claims and Disputes</h3>
          {F.YN('claim_received', 'Claim or Dispute Received?')}
          {get('claim_received') === 'Yes' && <>
            {F.Sel('claim_party', 'Claiming Party', ['Government', 'Contractor'])}
            {F.Money('claim_amount', 'Claim Amount')}
            {F.Date_('claim_date', 'Claim Date')}
            {F.Long('claim_basis', 'Claim Basis')}
            {F.YN('claim_certified', 'Certified Claim Required and Received?', ['Yes', 'No', 'Not Applicable'])}
            {F.Sel('claim_decision_status', "Contracting Officer's Final Decision Status", ['Draft', 'Legal Review', 'Issued'])}
            {F.YN('claim_appeal', 'Appeal or Litigation Pending?')}
          </>}
        </div>
        <div style={fs.section}>
          <h3 style={fs.title}>D. Termination</h3>
          {F.YN('term_considered', 'Termination Considered?')}
          {get('term_considered') === 'Yes' && <>
            {F.Sel('term_type', 'Termination Type', ['Convenience', 'Cause (commercial contract)', 'Default', 'Partial termination', 'Complete termination'])}
            {F.Long('term_rationale', 'Termination Rationale')}
            {F.YN('term_legal_review', 'Legal Review Completed?')}
            {F.YN('term_notice_issued', 'Termination Notice Issued?')}
            {F.Date_('term_effective', 'Effective Date')}
            {F.YN('term_settlement_req', 'Settlement Required?')}
            {F.Sel('term_settlement_status', 'Settlement Status', ['Pending', 'Negotiating', 'Completed'])}
            {F.YN('term_actions_complete', 'Termination Actions Complete?')}
          </>}
        </div>
      </>)}

      {/* ── Tab 6: Closeout ── */}
      {tab === 'closeout' && (<>
        <div style={fs.section}>
          <h3 style={fs.title}>A. Closeout Eligibility</h3>
          <p style={fs.sub}>FAR 4.804.</p>
          {F.YN('co_all_delivered', 'All Supplies Delivered?', ['Yes', 'No', 'Not Applicable'])}
          {F.YN('co_all_services', 'All Services Performed?', ['Yes', 'No', 'Not Applicable'])}
          {F.YN('co_all_deliverables', 'All Deliverables Accepted?')}
          {F.YN('co_options_done', 'All Option Periods Expired or Completed?', ['Yes', 'No', 'Not Applicable'])}
          {F.YN('co_litigation', 'Litigation or Appeal Pending?')}
          {F.YN('co_termination_outstanding', 'Termination Actions Outstanding?')}
          <div style={{ padding: '10px 14px', borderRadius: 6, marginTop: 8, fontSize: 13, fontWeight: 600,
            background: eligibleForCloseout ? '#f0fdf4' : '#fef2f2', border: `1px solid ${eligibleForCloseout ? '#bbf7d0' : '#fecaca'}`,
            color: eligibleForCloseout ? '#166534' : '#991b1b' }}>
            System determination: {eligibleForCloseout ? 'Eligible for closeout' : (blockers ? 'CANNOT close — litigation/appeal pending or termination actions outstanding' : 'Not yet physically complete')}
          </div>
        </div>
        <div style={fs.section}>
          <h3 style={fs.title}>B. Closeout Checklist</h3>
          {F.Checklist('closeout_checklist', CLOSEOUT_CHECKLIST)}
        </div>
        <div style={fs.section}>
          <h3 style={fs.title}>C. Closeout Time Standard</h3>
          <p style={fs.sub}>Configuration-controlled per FAR/agency rules.</p>
          {F.Sel('closeout_category', 'Contract Category', Object.keys(CLOSEOUT_STANDARDS))}
          {get('closeout_category') && (
            <div style={{ fontSize: 12, color: '#374151', background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 6, padding: 10 }}>
              <strong>FAR target:</strong> {CLOSEOUT_STANDARDS[get('closeout_category')]}
            </div>
          )}
        </div>
        <div style={fs.section}>
          <h3 style={fs.title}>D. Final Closeout Approval</h3>
          {F.Date_('physical_completion_date', 'Physical Completion Date')}
          {F.Date_('final_payment_date', 'Final Payment Date')}
          {F.Money('excess_deobligated', 'Excess Funds Deobligated')}
          {F.Long('closeout_exceptions', 'Closeout Exceptions')}
          {F.YN('co_certification', 'Contracting Officer Certification Complete?')}
          {F.Date_('closeout_date', 'Closeout Date')}
          {F.Sel('final_status', 'Final Status', ['Closed', 'Closed with Exceptions', 'Cannot Close'])}
        </div>
      </>)}

      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 40 }}>
        <button onClick={save} disabled={saving} style={{ padding: '10px 24px', background: saving ? '#86efac' : '#10b981', color: 'white', border: 'none', borderRadius: 6, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>{saving ? 'Saving...' : 'Save'}</button>
      </div>
    </div>
  );
}
